import { useEffect, useMemo, useState } from 'react';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { InfoCard, Link } from '@backstage/core-components';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import LaunchIcon from '@material-ui/icons/Launch';
import { Line, LineChart, ResponsiveContainer } from 'recharts';
import {
  DEFAULT_CHECK_WINDOW_MINUTES,
  HealthCheck,
  HealthStatus,
  MetricCheck,
  PingResult,
  DatadogSeries,
  UptimeCheck,
  defaultHealthChecks,
  fetchUptime,
  getSavedGraphs,
  metricStatus,
  queryTimeseries,
  runPings,
  windowedValue,
} from './api';

// ---------------------------------------------------------------------------
// Status palette (SAVR colors, tuned per theme).
// ---------------------------------------------------------------------------

const statusColor = (status: HealthStatus, dark: boolean) => {
  switch (status) {
    case 'ok':
      return dark ? '#34D399' : '#059669';
    case 'warn':
      return dark ? '#F5A623' : '#D97706';
    case 'crit':
      return dark ? '#FF385F' : '#E11D48';
    default:
      return dark ? '#8B8B93' : '#6B7280';
  }
};

const formatValue = (value: number) => {
  const abs = Math.abs(value);
  if (abs >= 1e9) return `${(value / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${(value / 1e3).toFixed(1)}k`;
  if (abs >= 100) return value.toFixed(0);
  if (abs >= 1) return `${Number(value.toFixed(2))}`;
  if (abs === 0) return '0';
  return value.toPrecision(2);
};

const formatAge = (iso: string) => {
  const seconds = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
};

// ---------------------------------------------------------------------------
// Data hooks
// ---------------------------------------------------------------------------

/** The entity's effective health checks: saved set, else annotation default. */
export function useHealthChecks(reloadKey = 0) {
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const apis = useMemo(
    () => ({ discoveryApi, fetchApi }),
    [discoveryApi, fetchApi],
  );
  const [state, setState] = useState<{
    loading: boolean;
    checks: HealthCheck[];
    saved: boolean;
  }>({ loading: true, checks: [], saved: false });

  useEffect(() => {
    let cancelled = false;
    getSavedGraphs(apis, entity)
      .then(result => {
        if (!cancelled) {
          setState({
            loading: false,
            checks: result.health ?? defaultHealthChecks(entity),
            saved: result.health !== null,
          });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setState({
            loading: false,
            checks: defaultHealthChecks(entity),
            saved: false,
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [apis, entity, reloadKey]);

  return { ...state, apis, entity };
}

/** Server-side ping results, refreshed every 60s and when checks change. */
function usePingResults(pingNames: string, reloadKey: number) {
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [results, setResults] = useState<Map<string, PingResult>>(new Map());

  useEffect(() => {
    if (!pingNames) {
      return undefined;
    }
    let cancelled = false;
    const load = () =>
      runPings({ discoveryApi, fetchApi }, entity)
        .then(list => {
          if (!cancelled) {
            setResults(new Map(list.map(result => [result.name, result])));
          }
        })
        .catch(() => undefined);
    load();
    const interval = setInterval(load, 60_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [discoveryApi, fetchApi, entity, pingNames, reloadKey]);

  return results;
}

/** Per-check uptime history for the current entity, keyed by check name. */
function useUptime(hasPings: boolean, reloadKey: number) {
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [byName, setByName] = useState<Map<string, UptimeCheck>>(new Map());

  useEffect(() => {
    if (!hasPings) {
      return undefined;
    }
    let cancelled = false;
    fetchUptime({ discoveryApi, fetchApi }, entity)
      .then(checks => {
        if (!cancelled) {
          setByName(new Map(checks.map(check => [check.name, check])));
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [discoveryApi, fetchApi, entity, hasPings, reloadKey]);

  return byName;
}

const pingNamesOf = (checks: HealthCheck[]) =>
  checks
    .filter(check => check.type === 'ping')
    .map(check => check.name)
    .join('|');

type MetricReading = {
  status: HealthStatus;
  value?: number;
  points: { ts: number; value: number }[];
};

/** Latest value + a short sparkline history for one metric check. */
function useMetricReading(check: MetricCheck, reloadKey: number) {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [reading, setReading] = useState<MetricReading>({
    status: 'unknown',
    points: [],
  });

  useEffect(() => {
    let cancelled = false;
    const to = Math.floor(Date.now() / 1000);
    const windowSeconds =
      (check.windowMinutes ?? DEFAULT_CHECK_WINDOW_MINUTES) * 60;
    queryTimeseries(
      { discoveryApi, fetchApi },
      check.query,
      to - windowSeconds,
      to,
    )
      .then((series: DatadogSeries[]) => {
        if (cancelled) {
          return;
        }
        const pointlist = series[0]?.pointlist ?? [];
        const points = pointlist
          .filter(([, v]) => v !== null)
          .map(([ts, v]) => ({ ts, value: v as number }));
        const value = windowedValue(
          points.map(point => point.value),
          check.aggregation,
        );
        setReading({ status: metricStatus(check, value), value, points });
      })
      .catch(() => {
        if (!cancelled) {
          setReading({ status: 'unknown', points: [] });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [discoveryApi, fetchApi, check, reloadKey]);

  return reading;
}

type CiRun = {
  conclusion: string | null;
  status: string | null;
  display_title?: string;
  name?: string;
  html_url: string;
  updated_at: string;
};

/** Latest GitHub Actions run; null while loading or when unavailable. */
function useLatestCiRun() {
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const slug = entity.metadata.annotations?.['github.com/project-slug'];
  const [run, setRun] = useState<CiRun | null>(null);

  useEffect(() => {
    const repo = slug?.startsWith('savrcom/')
      ? slug.slice('savrcom/'.length)
      : undefined;
    if (!repo) {
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const base = await discoveryApi.getBaseUrl('proxy');
        const response = await fetchApi.fetch(
          `${base}/github/savrcom/${repo}/actions/runs?per_page=1`,
        );
        if (!response.ok) {
          return; // e.g. external users without savr.com/external-actions
        }
        const body = await response.json();
        if (!cancelled && body?.workflow_runs?.[0]) {
          setRun(body.workflow_runs[0]);
        }
      } catch {
        /* hidden when unavailable */
      }
    };
    load();
    const interval = setInterval(load, 60_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [discoveryApi, fetchApi, slug]);

  return run;
}

const ciStatus = (run: CiRun): { status: HealthStatus; label: string } => {
  if (run.status !== 'completed') {
    return { status: 'unknown', label: 'Running' };
  }
  switch (run.conclusion) {
    case 'success':
      return { status: 'ok', label: 'Success' };
    case 'failure':
    case 'startup_failure':
    case 'timed_out':
      return { status: 'crit', label: 'Failed' };
    case 'cancelled':
      return { status: 'unknown', label: 'Cancelled' };
    default:
      return { status: 'warn', label: run.conclusion ?? 'Unknown' };
  }
};

// ---------------------------------------------------------------------------
// Presentation
// ---------------------------------------------------------------------------

const useStyles = makeStyles(theme => ({
  '@keyframes healthPulse': {
    '0%': { opacity: 1 },
    '50%': { opacity: 0.35 },
    '100%': { opacity: 1 },
  },
  strip: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: theme.spacing(2),
  },
  tile: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    borderRadius: 12,
    padding: '10px 14px',
    minWidth: 200,
    flex: '0 1 auto',
  },
  dot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    flexShrink: 0,
  },
  pulsing: {
    animation: '$healthPulse 1.6s ease-in-out infinite',
  },
  tileName: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
  },
  tileValue: {
    fontSize: 17,
    fontWeight: 700,
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
  },
  tileUnit: {
    fontSize: 11,
    fontWeight: 500,
    color: theme.palette.text.secondary,
    marginLeft: 3,
  },
  spark: {
    width: 76,
    height: 30,
    flexShrink: 0,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '7px 0',
  },
  rowName: {
    fontSize: 13,
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  rowValue: {
    fontSize: 13,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    marginLeft: 'auto',
  },
  rowSpark: {
    width: 64,
    height: 22,
    flexShrink: 0,
  },
  divider: {
    borderTop: `1px solid ${theme.palette.divider}`,
    margin: theme.spacing(1, 0),
  },
  ciTitle: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
  },
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    borderRadius: 999,
    padding: '2px 10px',
    fontSize: 12,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  empty: {
    fontSize: 13,
    color: theme.palette.text.secondary,
  },
  uptimeBars: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 2,
  },
  uptimeBar: {
    width: 4,
    height: 14,
    borderRadius: 1.5,
    flexShrink: 0,
    transition: 'transform 120ms ease',
    '&:hover': {
      transform: 'scaleY(1.3)',
    },
  },
  uptimeCaption: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 10,
    color: theme.palette.text.secondary,
    marginTop: 3,
  },
  tileUptime: {
    marginTop: 7,
  },
  rowUptime: {
    marginLeft: 'auto',
    flexShrink: 0,
  },
}));

const formatPct = (pct: number | null) => (pct === null ? '—' : `${pct}%`);

/**
 * A day's bar colour band: clean, nearly clean, or bad.
 *
 * The 1% line is what separates a day with the odd blip from a day worth
 * looking at. Named rather than nested inline so the three bands read as three
 * bands.
 *
 * Takes the two counts rather than a count and a rate: the rate is derived from
 * them, and a signature that accepted both would admit a pair that disagrees
 * (`failures = 0` with a non-zero rate) and quietly answer from the wrong one.
 * Callers guard `total > 0` before there is a day to colour at all.
 */
export const dayStatus = (failures: number, total: number): HealthStatus => {
  if (failures === 0) {
    return 'ok';
  }
  return failures / total < 0.01 ? 'warn' : 'crit';
};

/**
 * Statuspage-style availability history: one bar per day over the last 30
 * days (oldest left), colored by that day's ping failure rate. Fed by the
 * backend's minutely background pinger, so days before the feature shipped
 * render as "no data".
 */
export function UptimeBars({
  check,
  compact,
  className,
}: {
  check: UptimeCheck;
  compact?: boolean;
  className?: string;
}) {
  const classes = useStyles();
  const theme = useTheme();
  const dark = theme.palette.type === 'dark';
  const noData = dark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.12)';
  const { day, week, month } = check.availability;
  const summary = `Availability - 24h ${formatPct(day)} · 7d ${formatPct(
    week,
  )} · 30d ${formatPct(month)}`;
  return (
    <div className={className}>
      <div className={classes.uptimeBars} title={summary}>
        {check.days.map(entry => {
          let color = noData;
          let label = `${entry.date} · no data`;
          if (entry.total > 0) {
            const rate = entry.failures / entry.total;
            color = statusColor(dayStatus(entry.failures, entry.total), dark);
            const pct = Math.round((1 - rate) * 10000) / 100;
            label = `${entry.date} · ${pct}% (${entry.failures}/${entry.total} checks failed)`;
          }
          return (
            <span
              key={entry.date}
              title={label}
              className={classes.uptimeBar}
              style={{
                backgroundColor: color,
                ...(compact ? { width: 3, height: 11 } : {}),
              }}
            />
          );
        })}
      </div>
      {!compact && (
        <div className={classes.uptimeCaption}>
          <span>30 days</span>
          <span>{formatPct(month)} uptime</span>
        </div>
      )}
    </div>
  );
}

function Sparkline({
  points,
  color,
  className,
}: {
  points: { ts: number; value: number }[];
  color: string;
  className: string;
}) {
  if (points.length < 2) {
    return <div className={className} />;
  }
  return (
    <div className={className}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={points.slice(-30)}
          margin={{ top: 2, right: 0, bottom: 2, left: 0 }}
        >
          <Line
            dataKey="value"
            type="monotone"
            dot={false}
            strokeWidth={1.5}
            stroke={color}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function StatusDot({
  status,
  pulsing,
}: {
  status: HealthStatus;
  pulsing?: boolean;
}) {
  const classes = useStyles();
  const theme = useTheme();
  const color = statusColor(status, theme.palette.type === 'dark');
  return (
    <span
      className={`${classes.dot} ${pulsing ? classes.pulsing : ''}`}
      style={{ backgroundColor: color, boxShadow: `0 0 10px ${color}99` }}
    />
  );
}

const thresholdHint = (check: MetricCheck) =>
  `Unhealthy when ${check.alarmWhen} ${check.critical}${
    check.warning !== undefined
      ? ` (warning ${check.alarmWhen} ${check.warning})`
      : ''
  }`;

function MetricIndicator({
  check,
  variant,
  reloadKey,
}: {
  check: MetricCheck;
  variant: 'tile' | 'row';
  reloadKey: number;
}) {
  const classes = useStyles();
  const theme = useTheme();
  const reading = useMetricReading(check, reloadKey);
  const color = statusColor(reading.status, theme.palette.type === 'dark');
  const value =
    reading.value !== undefined
      ? formatValue(reading.value)
      : // With a configured no-data verdict the tile is colored, so say why.
        (check.noData && check.noData !== 'unknown' && 'No data') || '–';

  if (variant === 'row') {
    return (
      <Tooltip title={thresholdHint(check)}>
        <div className={classes.row}>
          <StatusDot status={reading.status} pulsing={false} />
          <span className={classes.rowName}>{check.name}</span>
          <span className={classes.rowValue} style={{ color }}>
            {value}
            {check.unit && (
              <span className={classes.tileUnit}>{check.unit}</span>
            )}
          </span>
          <Sparkline
            points={reading.points}
            color={color}
            className={classes.rowSpark}
          />
        </div>
      </Tooltip>
    );
  }
  return (
    <Tooltip title={thresholdHint(check)}>
      <div
        className={classes.tile}
        style={{
          backgroundColor: `${color}14`,
          border: `1px solid ${color}40`,
        }}
      >
        <StatusDot status={reading.status} pulsing={false} />
        <div>
          <div className={classes.tileName}>{check.name}</div>
          <div className={classes.tileValue} style={{ color }}>
            {value}
            {check.unit && (
              <span className={classes.tileUnit}>{check.unit}</span>
            )}
          </div>
        </div>
        <Sparkline
          points={reading.points}
          color={color}
          className={classes.spark}
        />
      </div>
    </Tooltip>
  );
}

function PingIndicator({
  name,
  result,
  uptime,
  variant,
}: {
  name: string;
  result?: PingResult;
  uptime?: UptimeCheck;
  variant: 'tile' | 'row';
}) {
  const classes = useStyles();
  const theme = useTheme();
  let status: HealthStatus = 'unknown';
  let value = '…';
  let detail: string | undefined;
  if (result) {
    status = result.ok ? 'ok' : 'crit';
    value = result.ok ? 'Up' : 'Down';
    if (result.ok) {
      detail = `${result.latencyMs} ms`;
    } else if (result.status) {
      detail = `HTTP ${result.status}`;
    }
  }
  const color = statusColor(status, theme.palette.type === 'dark');
  const showUptime = uptime && uptime.days.some(entry => entry.total > 0);

  if (variant === 'row') {
    return (
      <div className={classes.row}>
        <StatusDot status={status} pulsing={!result} />
        <span className={classes.rowName}>{name}</span>
        {showUptime && (
          <UptimeBars check={uptime} compact className={classes.rowUptime} />
        )}
        <span
          className={classes.rowValue}
          style={{ color, ...(showUptime ? { marginLeft: 0 } : {}) }}
        >
          {value}
          {detail && <span className={classes.tileUnit}>{detail}</span>}
        </span>
      </div>
    );
  }
  return (
    <div
      className={classes.tile}
      style={{ backgroundColor: `${color}14`, border: `1px solid ${color}40` }}
    >
      <StatusDot status={status} pulsing={!result} />
      <div>
        <div className={classes.tileName}>{name}</div>
        <div className={classes.tileValue} style={{ color }}>
          {value}
          {detail && <span className={classes.tileUnit}>{detail}</span>}
        </div>
        {showUptime && (
          <UptimeBars check={uptime} className={classes.tileUptime} />
        )}
      </div>
    </div>
  );
}

function CiIndicator({ variant }: { variant: 'tile' | 'row' }) {
  const classes = useStyles();
  const theme = useTheme();
  const run = useLatestCiRun();
  if (!run) {
    return null;
  }
  const { status, label } = ciStatus(run);
  const color = statusColor(status, theme.palette.type === 'dark');
  const pill = (
    <span
      className={classes.pill}
      style={{
        color,
        backgroundColor: `${color}1a`,
        border: `1px solid ${color}4d`,
      }}
    >
      <StatusDot status={status} pulsing={run.status !== 'completed'} />
      {label}
    </span>
  );

  if (variant === 'row') {
    return (
      <>
        <div className={classes.divider} />
        <div className={classes.row}>
          <span className={classes.rowName}>Latest CI run</span>
          {pill}
          <span className={classes.ciTitle}>
            {run.display_title ?? run.name}
          </span>
          <span className={classes.tileUnit}>{formatAge(run.updated_at)}</span>
          <Link to={run.html_url} noTrack>
            <LaunchIcon style={{ fontSize: 14, verticalAlign: 'middle' }} />
          </Link>
        </div>
      </>
    );
  }
  return (
    <div
      className={classes.tile}
      style={{ backgroundColor: `${color}14`, border: `1px solid ${color}40` }}
    >
      <StatusDot status={status} pulsing={run.status !== 'completed'} />
      <div>
        <div className={classes.tileName}>Latest CI run</div>
        <div className={classes.tileValue} style={{ color }}>
          {label}
          <span className={classes.tileUnit}>{formatAge(run.updated_at)}</span>
        </div>
      </div>
      <Link to={run.html_url} noTrack>
        <LaunchIcon style={{ fontSize: 16, verticalAlign: 'middle' }} />
      </Link>
    </div>
  );
}

/** The Observability tab's health strip. */
export function HealthStrip({
  checks,
  reloadKey,
}: {
  checks: HealthCheck[];
  reloadKey: number;
}) {
  const classes = useStyles();
  const pings = usePingResults(pingNamesOf(checks), reloadKey);
  const uptime = useUptime(
    checks.some(check => check.type === 'ping'),
    reloadKey,
  );
  if (checks.length === 0) {
    return (
      <div className={classes.strip}>
        <CiIndicator variant="tile" />
      </div>
    );
  }
  return (
    <div className={classes.strip}>
      {checks.map((check, index) =>
        check.type === 'ping' ? (
          <PingIndicator
            key={`${check.name}-${index}`}
            name={check.name}
            result={pings.get(check.name)}
            uptime={uptime.get(check.name)}
            variant="tile"
          />
        ) : (
          <MetricIndicator
            key={`${check.name}-${index}`}
            check={check}
            variant="tile"
            reloadKey={reloadKey}
          />
        ),
      )}
      <CiIndicator variant="tile" />
    </div>
  );
}

/** The Overview page's "Service health" card. */
export function ServiceHealthCard() {
  const classes = useStyles();
  const { checks, loading } = useHealthChecks();
  const pings = usePingResults(pingNamesOf(checks), 0);
  const uptime = useUptime(
    checks.some(check => check.type === 'ping'),
    0,
  );
  return (
    <InfoCard title="Service health">
      {!loading && checks.length === 0 && (
        <Typography className={classes.empty}>
          No health checks configured yet - add pings and metric thresholds from
          the Observability tab.
        </Typography>
      )}
      {checks.map((check, index) =>
        check.type === 'ping' ? (
          <PingIndicator
            key={`${check.name}-${index}`}
            name={check.name}
            result={pings.get(check.name)}
            uptime={uptime.get(check.name)}
            variant="row"
          />
        ) : (
          <MetricIndicator
            key={`${check.name}-${index}`}
            check={check}
            variant="row"
            reloadKey={0}
          />
        ),
      )}
      <CiIndicator variant="row" />
    </InfoCard>
  );
}
